import { useState } from 'react'
import { AlertCircle, ArrowLeft, Loader2 } from 'lucide-react'

import type { DealFormData } from '../../../lib/types'
import { ERC4626_SALE_TYPE } from '../../../lib/constants'
import { validateErc4626Vault } from '../../../lib/step-validators'
import { Button } from '../../ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../ui/card'
import { Checkbox } from '../../ui/checkbox'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'

interface StepErc4626VaultProps {
  formData: DealFormData
  onComplete: (data: Partial<DealFormData>) => void
  onUpdate: (data: Partial<DealFormData>) => void
  onBack: () => void
  onSubmitDeal?: (data?: Partial<DealFormData>) => void
  isSubmitting?: boolean
  submitLabel?: string
  inProgressLabel?: string
  onValidationErrors?: (errors: { id: string; label: string }[]) => void
  requestChanges?: Record<string, string>
}

const fieldLabels: Record<string, string> = {
  purchaseStartDate: 'Deposit Starts',
  vaultChainId: 'Deposit Chain ID',
  vaultAddress: 'Vault Address',
  depositTokenAddress: 'Underlying Asset Address',
  depositTokenDecimals: 'Underlying Asset Decimals',
  depositTokenName: 'Underlying Asset Name',
  depositTokenSymbol: 'Underlying Asset Symbol',
  vaultProtocolName: 'Protocol Name',
  vaultExternalUrl: 'External Vault URL',
}

export function StepErc4626Vault({
  formData,
  onComplete,
  onUpdate,
  onBack,
  onSubmitDeal,
  isSubmitting = false,
  submitLabel = 'Submit',
  inProgressLabel = 'Submitting...',
  onValidationErrors,
  requestChanges,
}: StepErc4626VaultProps) {
  const [localData, setLocalData] = useState({
    purchaseStartDate: formData.purchaseStartDate || '',
    vaultChainId: formData.vaultChainId || '',
    vaultAddress: formData.vaultAddress || '',
    depositTokenAddress: formData.depositTokenAddress || '',
    depositTokenDecimals: formData.depositTokenDecimals || '',
    depositTokenName: formData.depositTokenName || '',
    depositTokenSymbol: formData.depositTokenSymbol || '',
    depositScheduleTBD: formData.depositScheduleTBD ?? false,
    hideTimeline: formData.hideTimeline ?? true,
    requiresKyc: formData.requiresKyc ?? true,
    vaultProtocolName: formData.vaultProtocolName || '',
    vaultExternalUrl: formData.vaultExternalUrl || '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const updateField = (
    field: keyof typeof localData,
    value: string | boolean,
  ) => {
    const updatedData = { ...localData, [field]: value }
    setLocalData(updatedData)
    onUpdate(updatedData)
    if (errors[field]) {
      setErrors((previous) => {
        const next = { ...previous }
        delete next[field]
        return next
      })
    }
  }

  const renderError = (field: keyof typeof localData) => {
    const error = errors[field] || requestChanges?.[field]
    if (!error) return null
    return (
      <p className="text-status-error text-xs inline-flex items-center gap-1 mt-1">
        <AlertCircle className="w-3 h-3" />
        {error}
      </p>
    )
  }

  const handleSubmit = () => {
    const validation = validateErc4626Vault(localData)
    setErrors(validation.errors)

    if (!validation.isValid) {
      onValidationErrors?.(
        Object.keys(validation.errors).map((id) => ({
          id,
          label: fieldLabels[id] || id,
        })),
      )
      return
    }

    onValidationErrors?.([])
    onComplete(localData)
    onSubmitDeal?.(localData)
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Vault Contract</CardTitle>
          <CardDescription>
            Configure the on-chain ERC-4626 vault. The storefront will read live
            balances, allowance, total assets, share conversion, and deposit
            capacity directly from this chain.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-5 md:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="vaultChainId">Deposit Chain ID</Label>
              <Input
                id="vaultChainId"
                inputMode="numeric"
                value={localData.vaultChainId}
                onChange={(event) =>
                  updateField('vaultChainId', event.target.value)
                }
                placeholder="1"
                className={errors.vaultChainId ? 'border-status-error' : ''}
              />
              {renderError('vaultChainId')}
            </div>
            <div className="space-y-1">
              <Label htmlFor="vaultStandard">Vault Standard</Label>
              <Input id="vaultStandard" value="ERC4626" disabled />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="vaultAddress">Vault Address</Label>
            <Input
              id="vaultAddress"
              value={localData.vaultAddress}
              onChange={(event) =>
                updateField('vaultAddress', event.target.value)
              }
              placeholder="0x..."
              className={errors.vaultAddress ? 'border-status-error' : ''}
            />
            <p className="text-xs text-neutral-500">
              Enter the ERC-4626 vault address, not the underlying token.
            </p>
            {renderError('vaultAddress')}
          </div>

          <div className="space-y-1">
            <Label htmlFor="vaultProtocolName">Protocol Name</Label>
            <Input
              id="vaultProtocolName"
              value={localData.vaultProtocolName}
              onChange={(event) =>
                updateField('vaultProtocolName', event.target.value)
              }
              placeholder="IPOR Fusion"
              className={errors.vaultProtocolName ? 'border-status-error' : ''}
            />
            {renderError('vaultProtocolName')}
          </div>

          <div className="space-y-1">
            <Label htmlFor="vaultExternalUrl">
              External Vault Detail URL (optional)
            </Label>
            <Input
              id="vaultExternalUrl"
              type="url"
              value={localData.vaultExternalUrl}
              onChange={(event) =>
                updateField('vaultExternalUrl', event.target.value)
              }
              placeholder="https://..."
              className={errors.vaultExternalUrl ? 'border-status-error' : ''}
            />
            {renderError('vaultExternalUrl')}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Underlying Asset</CardTitle>
          <CardDescription>
            These values describe the token accepted by the vault. The address
            must exactly match the vault&apos;s on-chain asset() response.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-1">
            <Label htmlFor="depositTokenAddress">
              Underlying Asset Address
            </Label>
            <Input
              id="depositTokenAddress"
              value={localData.depositTokenAddress}
              onChange={(event) =>
                updateField('depositTokenAddress', event.target.value)
              }
              placeholder="0x..."
              className={
                errors.depositTokenAddress ? 'border-status-error' : ''
              }
            />
            {renderError('depositTokenAddress')}
          </div>

          <div className="grid gap-5 md:grid-cols-3">
            <div className="space-y-1 md:col-span-2">
              <Label htmlFor="depositTokenName">Asset Name</Label>
              <Input
                id="depositTokenName"
                value={localData.depositTokenName}
                onChange={(event) =>
                  updateField('depositTokenName', event.target.value)
                }
                placeholder="Wrapped Ether"
                className={errors.depositTokenName ? 'border-status-error' : ''}
              />
              {renderError('depositTokenName')}
            </div>
            <div className="space-y-1">
              <Label htmlFor="depositTokenSymbol">Symbol</Label>
              <Input
                id="depositTokenSymbol"
                value={localData.depositTokenSymbol}
                onChange={(event) =>
                  updateField('depositTokenSymbol', event.target.value)
                }
                placeholder="WETH"
                className={
                  errors.depositTokenSymbol ? 'border-status-error' : ''
                }
              />
              {renderError('depositTokenSymbol')}
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="depositTokenDecimals">Asset Decimals</Label>
            <Input
              id="depositTokenDecimals"
              inputMode="numeric"
              value={localData.depositTokenDecimals}
              onChange={(event) =>
                updateField('depositTokenDecimals', event.target.value)
              }
              placeholder="18"
              className={
                errors.depositTokenDecimals ? 'border-status-error' : ''
              }
            />
            {renderError('depositTokenDecimals')}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Sale Behavior</CardTitle>
          <CardDescription>
            The storefront discriminator is fixed to {ERC4626_SALE_TYPE}. Live
            vault state must not be copied into CMS metadata.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="purchaseStartDate">Deposit Starts (UTC)</Label>
            <Input
              id="purchaseStartDate"
              type="datetime-local"
              value={localData.purchaseStartDate}
              onChange={(event) =>
                updateField('purchaseStartDate', event.target.value)
              }
              className={errors.purchaseStartDate ? 'border-status-error' : ''}
            />
            <p className="text-xs text-neutral-500">
              This is the only timeline event stored for an open-ended vault.
            </p>
            {renderError('purchaseStartDate')}
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="hideTimeline"
              checked={localData.hideTimeline}
              onCheckedChange={(checked) =>
                updateField('hideTimeline', checked)
              }
              className="mt-1"
            />
            <div>
              <Label htmlFor="hideTimeline" className="cursor-pointer">
                Hide timeline
              </Label>
              <p className="text-xs text-neutral-500">
                Recommended for open-ended vaults.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="depositScheduleTBD"
              checked={localData.depositScheduleTBD}
              onCheckedChange={(checked) =>
                updateField('depositScheduleTBD', checked)
              }
              className="mt-1"
            />
            <div>
              <Label htmlFor="depositScheduleTBD" className="cursor-pointer">
                Deposit schedule is to be determined
              </Label>
              <p className="text-xs text-neutral-500">
                Leave off for an immediately available open-ended vault.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="requiresKyc"
              checked={localData.requiresKyc}
              onCheckedChange={(checked) => updateField('requiresKyc', checked)}
              className="mt-1"
            />
            <div>
              <Label htmlFor="requiresKyc" className="cursor-pointer">
                Requires KYC
              </Label>
              <p
                className={`text-xs ${
                  localData.requiresKyc
                    ? 'text-neutral-500'
                    : 'text-status-error'
                }`}
              >
                {localData.requiresKyc
                  ? 'Enabled by default.'
                  : 'KYC is disabled. Use this only after explicit approval for the sale.'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between pt-6">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          size="md"
          className="flex items-center gap-2 bg-transparent px-5"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          variant="brand"
          size="md"
          className="flex items-center gap-2 px-5"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {inProgressLabel}
            </>
          ) : (
            submitLabel
          )}
        </Button>
      </div>
    </div>
  )
}
